import { ApiResponse } from '../types';
import apiService from './api';

type FeedbackValue = 'up' | 'down';
type FeedbackNullable = FeedbackValue | null;

const FEEDBACK_FLAG_PREFIX = 'message_feedback:';

function getStorageKey(messageId: string): string {
  return `${FEEDBACK_FLAG_PREFIX}${messageId}`;
}

export function getMessageFeedback(messageId: string): FeedbackNullable {
  try {
    const v = localStorage.getItem(getStorageKey(messageId));
    if (v === 'up' || v === 'down') return v;
    return null;
  } catch {
    return null;
  }
}

function setLocalFeedback(messageId: string, value: FeedbackNullable): void {
  try {
    if (value === null) {
      localStorage.removeItem(getStorageKey(messageId));
    } else {
      localStorage.setItem(getStorageKey(messageId), value);
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Set feedback for a message. Allows changing or clearing feedback.
 * - value: 'up' | 'down' | null (null clears the feedback)
 */
export async function setMessageFeedback(messageId: string, value: FeedbackNullable): Promise<boolean> {
  if (!messageId) return false;

  const previous = getMessageFeedback(messageId);
  // Optimistically store
  setLocalFeedback(messageId, value);

  // Best-effort POST; ignore failures
  try {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const token = apiService.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // Preferred: upsert message feedback
    const fbRes = await fetch(`${baseURL}/messages/${encodeURIComponent(messageId)}/feedback`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ value }), // value can be null
    });
    if (!fbRes.ok) {
      // Fallback: generic analytics event
      await fetch(`${baseURL}/analytics/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type: 'message_feedback_set',
          message_id: messageId,
          previous,
          value,
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    }
  } catch {
    // swallow network errors; we already marked locally
  }

  return true;
}

/**
 * Backwards-compat: record a single feedback event (no clearing). Kept for callers expecting old API.
 */
export async function trackMessageFeedback(messageId: string, value: FeedbackValue): Promise<boolean> {
  if (!messageId) return false;
  // Route to set API (does not block repeats)
  try {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const token = apiService.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${baseURL}/analytics/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        type: 'message_feedback',
        message_id: messageId,
        value,
        ts: new Date().toISOString(),
      }),
    });
    await res.json().catch(() => {});
  } catch {
    // ignore
  }

  return true;
}


